import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTeam } from '../context/TeamContext';
import './PlayerSearch.css';

// Search bar in the top menu: type 2+ letters of any name, pick a result to open that player's
// hitter or pitcher page. Two-way players appear once per role; your own team's players are
// listed first and tagged "My Team".
const MIN_CHARS = 2;
const WAIT_MS = 60; // just enough to merge a burst of keystrokes; the API answers from memory

export default function PlayerSearch() {
  const myTeam = (useTeam().selectedTeam?.name || '').trim().toLowerCase();
  const go = useNavigate();
  const box = useRef(null);
  const [text, setText] = useState('');
  const [hits, setHits] = useState([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const term = text.trim();

  useEffect(() => {
    if (term.length < MIN_CHARS) { setHits([]); setBusy(false); return undefined; }
    setBusy(true);
    const stop = new AbortController();
    const wait = setTimeout(() => {
      fetch(`/api/players/search?q=${encodeURIComponent(term)}`, { signal: stop.signal })
        .then((r) => r.json())
        .then((d) => { setHits(d.success ? d.players : []); setBusy(false); })
        .catch((e) => { if (e.name !== 'AbortError') { setHits([]); setBusy(false); } });
    }, WAIT_MS);
    return () => { clearTimeout(wait); stop.abort(); }; // a newer search replaces an older one
  }, [term]);

  useEffect(() => {
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', away);
    return () => document.removeEventListener('mousedown', away);
  }, []);

  // Roster team names can differ slightly from the display name, so match loosely.
  const options = useMemo(() => {
    const mine = (team) => {
      const t = (team || '').trim().toLowerCase();
      return !!myTeam && !!t && (t === myTeam || t.includes(myTeam) || myTeam.includes(t));
    };
    const list = hits.flatMap((p) => {
      const roles = [p.isBatter && 'hitter', p.isPitcher && 'pitcher'].filter(Boolean);
      return (roles.length ? roles : ['hitter']).map((role) => ({ ...p, role, mine: mine(p.team) }));
    });
    return [...list.filter((o) => o.mine), ...list.filter((o) => !o.mine)];
  }, [hits, myTeam]);

  const open_ = (o) => {
    setOpen(false);
    setText('');
    setHits([]);
    const tm = Math.trunc(Number(o.trackmanPlayerId)) > 0 ? Math.trunc(Number(o.trackmanPlayerId)) : '';
    const q = new URLSearchParams({ name: o.fullName || '', team: o.team || '', tm: String(tm) });
    // TrackMan-only players (summer ball, high school, out of roster) have no roster playerId -- 0
    go(`/player/${o.role}/${encodeURIComponent(o.playerId ?? 0)}?${q}`);
  };

  return (
    <div className="player-search" ref={box}>
      <input className="player-search-input" placeholder="Search any player…" value={text}
        onFocus={() => setOpen(true)} onChange={(e) => { setText(e.target.value); setOpen(true); }} />
      {open && term.length >= MIN_CHARS && (
        <div className="player-search-dropdown">
          {busy && <div className="player-search-status">Searching…</div>}
          {!busy && !options.length && <div className="player-search-status">No players found</div>}
          {!busy && options.map((o) => (
            <button key={`${o.playerId ?? `tm${o.trackmanPlayerId}`}-${o.role}`} type="button" className="player-search-item" onClick={() => open_(o)}>
              <span className="player-search-name">{o.fullName}{o.mine && <span className="player-search-myteam">My Team</span>}</span>
              <span className="player-search-meta">
                {o.team && <span className="player-search-team">{o.team}</span>}
                <span className={`player-search-role player-search-role-${o.role}`}>{o.role === 'pitcher' ? 'Pitcher' : 'Hitter'}</span>
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
